import {PaymentElement, useElements, useStripe} from "@stripe/react-stripe-js";
import React, {useEffect, useState} from "react";
import {
    llmEngineBaseURLSelector,
    Message,
    reservationCurrencySelector,
    reservationPriceSelector,
    sendConfirmStripeIntent,
    sendMessageToBot,
    sendStripeIntent,
    stripeClientSecretSelector,
    stripeErrorMessageSelector,
    StripePaymentState,
} from "../../chatbotSlice";
import {useAppDispatch, useAppSelector} from "../../../app/hooks";
import {getFullPriceString} from "../../reservationUtil";

interface BotCheckoutFormProps {
    message: Message
}

export const BotCheckoutForm: React.FC<BotCheckoutFormProps> = ({message}) => {
    const stripe = useStripe();
    const elements = useElements();
    const dispatch = useAppDispatch();

    const llmEngineBaseURL = useAppSelector(llmEngineBaseURLSelector);
    const stripeClientSecret = useAppSelector(stripeClientSecretSelector);
    const stripeErrorMessage = useAppSelector(stripeErrorMessageSelector);

    const reservationPrice = useAppSelector(reservationPriceSelector);
    const reservationCurrency = useAppSelector(reservationCurrencySelector);

    const priceText = `${getFullPriceString(reservationPrice, reservationCurrency)}`

    const [errorMessage, setErrorMessage] = useState<string>();

    const handleSubmit = async (event: any) => {
        event.preventDefault();

        if (elements == null) {
            return;
        }

        // Trigger form validation and wallet collection
        const {error: submitError} = await elements.submit();
        if (submitError) {
            // Show error to your customer
            setErrorMessage(submitError.message);
            return;
        }

        dispatch(sendStripeIntent(message.stripePaymentId!));
    }

    useEffect(() => {
        if (stripeClientSecret) {
            stripe?.confirmPayment({
                //`Elements` instance that was used to create the Payment Element
                elements: elements ? elements : undefined,
                clientSecret: stripeClientSecret,
                confirmParams: {
                    return_url: `${llmEngineBaseURL}/stripe/confirm-intent?secret=${stripeClientSecret}`,
                },
                redirect: "if_required"
            }).then((stripeResponse) => {
                if (stripeResponse.error) {
                    setErrorMessage(stripeResponse.error.message);
                } else {
                    dispatch(sendConfirmStripeIntent());
                    dispatch(sendMessageToBot(`All right. I just paid ${priceText} to reserve.`));
                }
            });
        } else if (stripeErrorMessage) {
            setErrorMessage(stripeErrorMessage);
        }
    }, [stripeClientSecret])

    const getButtonTitle = () => {
        switch (message.stripePaymentState) {
            case StripePaymentState.READY:
                return `Reserve now for ${priceText}`
            case StripePaymentState.PROCESSING:
                return "Processing..."
            case StripePaymentState.PAID:
                return "Reserved"
        }
    }

    return <form onSubmit={handleSubmit}>
        <PaymentElement/>
        {errorMessage && <div>{errorMessage}</div>}
        <button
            type="submit"
            disabled={!stripe || !elements || message.stripePaymentState == StripePaymentState.PAID}
            className="my-6 text-zinc-950 text-sm font-semibold text-white w-full px-4 py-2 bg-[#131313] cursor-pointer hover:bg-[#272727] hover:cursor-pointer flex justify-center border border-black items-center self-center rounded-full"
        >
            {getButtonTitle()}
        </button>
    </form>
};
